import { useEffect, useState } from "react";
import { FeedSkeleton, SentimentSkeleton } from "./CoTraderSkeletons";
import { TimeSince } from "./Utilities/relativeTime";
import { useResponsiveTileTypography } from "./Utilities/useResponsiveTileTypography";
import { formatUserClockTime, formatUserTimeWithZone } from "./Utilities/timezone";
import {
  formatScore,
  getQuestion,
  getUpdateDisplay,
} from "./Utilities/updateDisplay";
import "./CoTrader.css";

// Drives only the "Due in" countdowns; arrival times tick via <TimeSince />.
const CLOCK_TICK_MS = 30 * 1000;
const TREND_EPSILON = 0.03;

const getSentimentReading = (value) => {
  if (value == null) return { tone: "idle", label: "Awaiting data" };

  const magnitude = Math.abs(value);
  if (magnitude < 0.05) return { tone: "neutral", label: "Neutral" };

  const direction = value > 0 ? "bullish" : "bearish";
  const tone = value > 0 ? "positive" : "negative";
  if (magnitude < 0.35) return { tone, label: `Slightly ${direction}` };
  if (magnitude < 0.7) return { tone, label: direction[0].toUpperCase() + direction.slice(1) };
  return { tone, label: `Strongly ${direction}` };
};

const getTrend = (delta) => {
  if (delta == null || Math.abs(delta) < TREND_EPSILON) return null;
  return delta > 0
    ? { label: "Improving", icon: "fa-arrow-trend-up", tone: "up" }
    : { label: "Softening", icon: "fa-arrow-trend-down", tone: "down" };
};

export const SentimentMeter = ({ value, delta, updatedAt }) => {
  const reading = getSentimentReading(value);
  const position = value == null ? 50 : ((value + 1) / 2) * 100;
  const trend = getTrend(delta);

  return (
    <div className={`ct-sentiment ct-sentiment--${reading.tone}`}>
      <div>
        <div className="ct-label">Market Sentiment</div>
        <div
          className="ct-sentiment-value"
          title={value == null ? undefined : value.toFixed(4)}
        >
          {value == null ? "—" : formatScore(value)}
        </div>
      </div>

      <div>
        <div
          className="ct-sentiment-track"
          role="meter"
          aria-label="Market sentiment"
          aria-valuemin={-1}
          aria-valuemax={1}
          aria-valuenow={value ?? undefined}
          aria-valuetext={reading.label}
        >
          {value != null && (
            <span className="ct-sentiment-marker" style={{ left: `${position}%` }} />
          )}
        </div>
        <div className="ct-sentiment-scale" aria-hidden="true">
          <span>
            Bearish<span>−1</span>
          </span>
          <span>
            Neutral<span>0</span>
          </span>
          <span>
            Bullish<span>+1</span>
          </span>
        </div>
      </div>

      <div className="ct-sentiment-meta">
        <span className="ct-sentiment-badge">{reading.label}</span>
        {updatedAt == null ? (
          <span className="ct-sentiment-updated">Waiting for stream</span>
        ) : (
          <span
            className="ct-sentiment-updated"
            title={`Updated ${formatUserTimeWithZone(updatedAt)}`}
          >
            {trend && (
              <span className={`ct-sentiment-trend is-${trend.tone}`}>
                <i className={`fa-solid ${trend.icon}`} aria-hidden="true" />
                {trend.label}
              </span>
            )}
            <span>{formatUserClockTime(updatedAt)}</span>
          </span>
        )}
      </div>
    </div>
  );
};

const renderStatus = (display) => {
  if (!display.status) return null;
  if (display.tone !== "upcoming") return display.status;

  // "Due in 46m" with the countdown emphasised.
  const [lead, ...rest] = display.status.split(" in ");
  return rest.length ? (
    <>
      {lead} in <strong>{rest.join(" in ")}</strong>
    </>
  ) : (
    display.status
  );
};

export const UpdateCard = ({ update, nowMs, onAskQuestion }) => {
  const display = getUpdateDisplay(update, nowMs);
  const question = getQuestion(update);
  const canAsk = question && typeof onAskQuestion === "function";

  return (
    <article className={`ct-update ct-update--${display.tone}`}>
      <div className="ct-update-head">
        <span className="ct-update-kind">
          <i className={display.icon} aria-hidden="true" />
          {display.kind}
        </span>
        <TimeSince className="ct-update-time" timestamp={display.timestamp} />
      </div>
      <h3 className="ct-update-title">{update.title || "Market update"}</h3>
      {update.description && update.type !== "count_down" && (
        <p className="ct-update-desc">{update.description}</p>
      )}
      {display.status && (
        <div className="ct-update-foot">
          <span className="ct-update-status">{renderStatus(display)}</span>
        </div>
      )}
      {canAsk && (
        <button
          type="button"
          className="ct-update-ask"
          onClick={() => onAskQuestion(question)}
        >
          <i className="fa-regular fa-comment" aria-hidden="true" />
          <span>{question}</span>
          <i className="fa-solid fa-arrow-right" aria-hidden="true" />
        </button>
      )}
    </article>
  );
};

// Content keys keep each card's identity when new updates are prepended;
// repeats get an occurrence suffix so keys stay unique.
const getUpdateKeys = (updates) => {
  const seen = new Map();
  return updates.map((update, index) => {
    const base =
      [update.title, update.timestamp, update.start_date, update.type]
        .filter((part) => part != null && part !== "")
        .join("|") || `idx-${index}`;
    const count = seen.get(base) || 0;
    seen.set(base, count + 1);
    return count ? `${base}#${count}` : base;
  });
};

/**
 * Insights module for the CoTrader desk: underlined tabs, the live sentiment
 * meter, and the streamed update feed. Non-feed tabs render `renderTab(key)`.
 */
const CoTraderInsightsPanel = ({
  tabs,
  activeTab,
  onTabChange,
  sentimentValue,
  sentimentDelta,
  sentimentUpdatedAt,
  loading,
  updates,
  scrollRef,
  onAskQuestion,
  renderTab,
  isFocused = false,
}) => {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const { containerRef, tier } = useResponsiveTileTypography();
  const isFeedTab = activeTab === "updates";
  const updateKeys = getUpdateKeys(updates);

  useEffect(() => {
    const timerId = window.setInterval(() => setNowMs(Date.now()), CLOCK_TICK_MS);
    return () => window.clearInterval(timerId);
  }, []);

  return (
    <section
      ref={containerRef}
      data-tier={tier}
      className={`ct-panel ct-insights ${isFocused ? "is-focused" : ""}`}
    >
      {tabs.length > 1 ? (
        <div className="ct-tabs" role="tablist" aria-label="Market insights">
          {tabs.map(([key, label]) => (
            <button
              type="button"
              key={key}
              role="tab"
              aria-selected={activeTab === key}
              className={`ct-tab ${activeTab === key ? "is-active" : ""}`}
              onClick={() => onTabChange(key)}
            >
              {label}
            </button>
          ))}
        </div>
      ) : (
        <div className="ct-tabs">
          <h2 className="ct-tab-title">{tabs[0]?.[1]}</h2>
        </div>
      )}

      {isFeedTab && loading && sentimentValue == null && <SentimentSkeleton />}
      {isFeedTab && !(loading && sentimentValue == null) && (
        <SentimentMeter
          value={sentimentValue}
          delta={sentimentDelta}
          updatedAt={sentimentUpdatedAt}
        />
      )}

      <div ref={isFeedTab ? scrollRef : null} className="ct-feed">
        {!isFeedTab ? (
          renderTab?.(activeTab)
        ) : loading ? (
          <FeedSkeleton />
        ) : updates.length > 0 ? (
          updates.map((update, index) => (
            <UpdateCard
              key={updateKeys[index]}
              update={update}
              nowMs={nowMs}
              onAskQuestion={onAskQuestion}
            />
          ))
        ) : (
          <div className="ct-feed-empty">
            <i className="fa-solid fa-satellite-dish" aria-hidden="true" />
            Generating live insights…
          </div>
        )}
      </div>
    </section>
  );
};

export default CoTraderInsightsPanel;
